import { Check, ChevronDown, Lock, Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";

export type Harness = "claude" | "pi-shim";

export interface ModelOption {
  id: string;
  label: string;
  provider: string;
  harness: Harness;
}

const CLAUDE_MODELS: ModelOption[] = [
  { id: "sonnet", label: "Sonnet", provider: "Claude Code", harness: "claude" },
  { id: "haiku", label: "Haiku", provider: "Claude Code", harness: "claude" },
  { id: "opus", label: "Opus", provider: "Claude Code", harness: "claude" },
];

// Only models we know pi-shim's vendored bundle ships with AND we have
// API keys for in production. The full pi-shim model database has hundreds
// of entries — any rondel deploy missing the matching provider key (e.g.
// GOOGLE_API_KEY for Gemini) will surface a "Missing API key for provider"
// error at first message, so we keep the picker narrow.
//
// TODO: replace with backend-driven /api/models/available once we want to
// gate by configured providers per deploy instead of compiling them in.
const PI_MODELS: ModelOption[] = [
  { id: "anthropic/claude-sonnet-4-6", label: "Claude Sonnet 4.6", provider: "Anthropic", harness: "pi-shim" },
  { id: "anthropic/claude-haiku-4-5", label: "Claude Haiku 4.5", provider: "Anthropic", harness: "pi-shim" },
  { id: "anthropic/claude-opus-4-6", label: "Claude Opus 4.6", provider: "Anthropic", harness: "pi-shim" },
  { id: "openai/gpt-5.4", label: "GPT-5.4", provider: "OpenAI", harness: "pi-shim" },
  { id: "openai/gpt-5.4-pro", label: "GPT-5.4 Pro", provider: "OpenAI", harness: "pi-shim" },
  { id: "openai/gpt-5-mini", label: "GPT-5 Mini", provider: "OpenAI", harness: "pi-shim" },
  { id: "openai/gpt-5-nano", label: "GPT-5 Nano", provider: "OpenAI", harness: "pi-shim" },
  { id: "openai/gpt-4o-mini", label: "GPT-4o Mini", provider: "OpenAI", harness: "pi-shim" },
];

export const ALL_MODELS = [...CLAUDE_MODELS, ...PI_MODELS];
export const DEFAULT_MODEL: ModelOption = CLAUDE_MODELS[0];

export function findModel(id: string): ModelOption | undefined {
  return ALL_MODELS.find((m) => m.id === id);
}

export function modelsForHarness(harness: Harness): ModelOption[] {
  return harness === "claude" ? CLAUDE_MODELS : PI_MODELS;
}

const HARNESS_META: Record<Harness, { label: string }> = {
  claude: { label: "Claude Code" },
  "pi-shim": { label: "Pi Agent" },
};

const PROVIDER_COLOR: Record<string, string> = {
  "Claude Code": "var(--warm)",
  Anthropic: "var(--warm)",
  OpenAI: "var(--ok)",
  Google: "var(--info)",
  Cerebras: "var(--accent)",
};

interface ModelSelectorProps {
  value: ModelOption;
  onChange: (model: ModelOption) => void;
  lockedHarness?: Harness | null;
}

export function ModelSelector({ value, onChange, lockedHarness }: ModelSelectorProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setSearch("");
      setTimeout(() => searchRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const available = lockedHarness ? modelsForHarness(lockedHarness) : ALL_MODELS;
  const filtered = search
    ? available.filter(
        (m) =>
          m.label.toLowerCase().includes(search.toLowerCase()) ||
          m.provider.toLowerCase().includes(search.toLowerCase()) ||
          m.id.toLowerCase().includes(search.toLowerCase()),
      )
    : available;

  const grouped: Record<string, ModelOption[]> = {};
  for (const m of filtered) {
    const key = lockedHarness
      ? m.provider
      : `${HARNESS_META[m.harness].label} — ${m.provider}`;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(m);
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-2 py-1 text-[11px] rounded border"
        style={{
          background: "var(--surface)",
          borderColor: "var(--border)",
          color: "var(--t2)",
        }}
      >
        {lockedHarness && <Lock size={9} style={{ color: "var(--t4)" }} />}
        <span
          className="font-medium"
          style={{
            color: PROVIDER_COLOR[value.provider] ?? "var(--t2)",
          }}
        >
          {value.label}
        </span>
        <span className="text-[9px]" style={{ color: "var(--t4)" }}>
          {value.provider}
        </span>
        <ChevronDown
          size={10}
          style={{
            transform: open ? "rotate(180deg)" : undefined,
            transition: "transform 0.15s",
          }}
        />
      </button>

      {open && (
        <div
          className="absolute bottom-full left-0 mb-1 w-72 rounded-lg max-h-80 flex flex-col z-50 animate-fade-in"
          style={{
            background: "var(--white)",
            border: "1px solid var(--border)",
            boxShadow: "0 8px 24px rgba(26, 18, 16, 0.08)",
          }}
        >
          <div
            className="flex items-center gap-1.5 px-2.5 py-2 border-b"
            style={{ borderColor: "var(--blt)" }}
          >
            <Search size={11} style={{ color: "var(--t4)" }} />
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search models…"
              className="flex-1 bg-transparent text-xs focus:outline-none"
              style={{ color: "var(--t1)" }}
            />
          </div>
          {lockedHarness && (
            <div
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] border-b"
              style={{
                background: "var(--surface)",
                color: "var(--t3)",
                borderColor: "var(--blt)",
              }}
            >
              <Lock size={9} />
              Locked to {HARNESS_META[lockedHarness].label} for this session
            </div>
          )}
          <div className="flex-1 overflow-y-auto p-1">
            {Object.entries(grouped).map(([group, models]) => (
              <div key={group}>
                <div
                  className="px-2.5 pt-2 pb-1 text-[10px] font-semibold uppercase"
                  style={{
                    letterSpacing: "0.08em",
                    color: PROVIDER_COLOR[group.split(" — ").pop() ?? ""] ?? "var(--t4)",
                  }}
                >
                  {group}
                </div>
                {models.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => {
                      onChange(m);
                      setOpen(false);
                    }}
                    className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded text-xs text-left"
                    style={{
                      background:
                        m.id === value.id
                          ? "color-mix(in srgb, var(--accent) 10%, transparent)"
                          : "transparent",
                      color:
                        m.id === value.id ? "var(--accent)" : "var(--t2)",
                    }}
                    onMouseEnter={(e) => {
                      if (m.id !== value.id) {
                        (e.currentTarget as HTMLElement).style.background =
                          "var(--hover)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (m.id !== value.id) {
                        (e.currentTarget as HTMLElement).style.background =
                          "transparent";
                      }
                    }}
                  >
                    <span className="flex-1 truncate">{m.label}</span>
                    <span
                      className="text-[9px]"
                      style={{ color: "var(--t4)", fontFamily: "var(--font-mono)" }}
                    >
                      {m.id}
                    </span>
                    {m.id === value.id && (
                      <Check size={11} style={{ color: "var(--accent)" }} />
                    )}
                  </button>
                ))}
              </div>
            ))}
            {filtered.length === 0 && (
              <p
                className="px-3 py-4 text-xs text-center"
                style={{ color: "var(--t4)" }}
              >
                No models match
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
